/* =========================================================
   Users & roles — the manager first, then the permission
   matrix and the per-role scope summary.

   Instructors are not a portal role: they hold no accounts and
   do not appear as a column.
   ========================================================= */
import type { ReactNode } from 'react';
import { DataTable, PageHeader, type Column } from '@/components/ui';
import { useI18n } from '@/i18n';
import { UserManager } from './UserManager';

type Cell = 'yes' | 'no' | 'self' | 'own' | 'scope' | 'all' | 'org' | 'n/a';

type PermRow = {
  perm: string;
  ETC_ADMIN: Cell;
  PARTNER_MGR: Cell;
  SITE_MGR: Cell;
};

type MatrixRow = PermRow & { _key: string };

export function UsersScreen() {
  const { lang, t } = useI18n();

  const matrix: PermRow[] = [
    { perm: lang === 'es' ? 'Ver dashboard' : 'View dashboard',                                 ETC_ADMIN: 'all', PARTNER_MGR: 'scope', SITE_MGR: 'scope' },
    { perm: lang === 'es' ? 'Editar campos amarillos (org)' : 'Edit yellow fields (org)',       ETC_ADMIN: 'yes', PARTNER_MGR: 'yes',   SITE_MGR: 'no' },
    { perm: lang === 'es' ? 'Editar campos amarillos (site)' : 'Edit yellow fields (site)',     ETC_ADMIN: 'yes', PARTNER_MGR: 'yes',   SITE_MGR: 'no' },
    { perm: lang === 'es' ? 'Editar datos personales (instructor)' : 'Edit instructor personal',ETC_ADMIN: 'yes', PARTNER_MGR: 'scope', SITE_MGR: 'no' },
    { perm: lang === 'es' ? 'Ver contacto legal' : 'View legal contact',                        ETC_ADMIN: 'all', PARTNER_MGR: 'yes',   SITE_MGR: 'yes' },
    { perm: lang === 'es' ? 'Solicitar cambio contacto legal' : 'Request legal contact change', ETC_ADMIN: 'n/a', PARTNER_MGR: 'yes',   SITE_MGR: 'no' },
    { perm: lang === 'es' ? 'Aprobar cambio contacto legal' : 'Approve legal contact change',   ETC_ADMIN: 'yes', PARTNER_MGR: 'no',    SITE_MGR: 'no' },
    { perm: lang === 'es' ? 'Solicitar desasociación' : 'Request disassociation',               ETC_ADMIN: 'yes', PARTNER_MGR: 'yes',   SITE_MGR: 'no' },
    { perm: lang === 'es' ? 'Autorizar desasociación' : 'Authorize disassociation',             ETC_ADMIN: 'yes', PARTNER_MGR: 'no',    SITE_MGR: 'no' },
    { perm: lang === 'es' ? 'Abrir/cerrar proceso' : 'Open/close process',                      ETC_ADMIN: 'yes', PARTNER_MGR: 'no',    SITE_MGR: 'no' },
    { perm: lang === 'es' ? 'Ver instructores desasociados' : 'View disassociated instructors', ETC_ADMIN: 'yes', PARTNER_MGR: 'no',    SITE_MGR: 'no' },
    { perm: lang === 'es' ? 'Cargar Compliance doc’s' : 'Upload compliance docs',               ETC_ADMIN: 'yes', PARTNER_MGR: 'no',    SITE_MGR: 'no' },
    { perm: lang === 'es' ? 'Verificar Compliance doc’s' : 'Verify compliance docs',            ETC_ADMIN: 'yes', PARTNER_MGR: 'yes',   SITE_MGR: 'no' },
    { perm: lang === 'es' ? 'Importar desde Excel' : 'Import from Excel',                       ETC_ADMIN: 'yes', PARTNER_MGR: 'no',    SITE_MGR: 'no' },
    { perm: lang === 'es' ? 'Gestionar usuarios/roles' : 'Manage users/roles',                  ETC_ADMIN: 'yes', PARTNER_MGR: 'no',    SITE_MGR: 'no' },
  ];

  const chipSm = { fontSize: 10, padding: '1px 7px' };
  const cell = (v: Cell): ReactNode => {
    if (v === 'yes') return <span className="chip ok" style={chipSm}>{t('common.yes')}</span>;
    if (v === 'no') return <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('common.no')}</span>;
    if (v === 'self') return <span className="chip brand" style={chipSm}>{lang === 'es' ? 'Solo propio' : 'Self only'}</span>;
    if (v === 'own') return <span className="chip brand" style={chipSm}>{lang === 'es' ? 'Solo del site' : 'Own site'}</span>;
    if (v === 'scope') return <span className="chip brand" style={chipSm}>{lang === 'es' ? 'En alcance' : 'In scope'}</span>;
    if (v === 'all') return <span className="chip ok" style={chipSm}>{lang === 'es' ? 'Todo' : 'All'}</span>;
    if (v === 'org') return <span className="chip brand" style={chipSm}>{lang === 'es' ? 'Su org' : 'Their org'}</span>;
    return <span className="chip" style={chipSm}>{v}</span>;
  };

  const columns: Column<MatrixRow>[] = [
    { key: 'perm', label: lang === 'es' ? 'Permiso' : 'Permission', render: (r) => <span style={{ fontWeight: 600 }}>{r.perm}</span> },
    { key: 'ETC_ADMIN', label: t('roles.ETC_ADMIN'), align: 'center', render: (r) => cell(r.ETC_ADMIN) },
    { key: 'PARTNER_MGR', label: t('roles.PARTNER_MGR'), align: 'center', render: (r) => cell(r.PARTNER_MGR) },
    { key: 'SITE_MGR', label: t('roles.SITE_MGR'), align: 'center', render: (r) => cell(r.SITE_MGR) },
  ];

  const scopes = [
    {
      role: 'ETC_ADMIN',
      scope: lang === 'es'
        ? 'Todas las organizaciones; gestiona usuarios y roles; abre/cierra el proceso de actualización; ve desasociados y autoriza sus bajas; procesa solicitudes de cambio legal; publica Compliance doc’s; importa Excel.'
        : 'All organizations; manages users/roles; opens/closes the update process; views disassociated instructors and authorizes their removal; processes legal change requests; publishes compliance docs; Excel imports.',
    },
    {
      role: 'PARTNER_MGR',
      scope: lang === 'es'
        ? 'Una o varias organizaciones + todos sus sites + instructores asignados. Edita campos amarillos y verifica Compliance doc’s.'
        : 'One or more organizations + all their sites + assigned instructors. Edits yellow fields and verifies compliance docs.',
    },
    {
      role: 'SITE_MGR',
      scope: lang === 'es'
        ? 'Su(s) site(s) y los instructores asignados a ellos. Solo lectura: no puede modificar datos ni solicitar desasociaciones.'
        : 'Their site(s) and instructors assigned to them. Read-only: cannot modify data or request disassociations.',
    },
  ];

  return (
    <div className="screen-enter" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <PageHeader
        title={t('users.title')}
        sub={lang === 'es' ? 'Gestiona usuarios, accesos y permisos por rol' : 'Manage users, sign-in and role permissions'}
      />

      <UserManager />

      <div style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 6 }}>
        {t('users.matrix')}
      </div>
      <DataTable columns={columns} rows={matrix.map((row, i) => ({ ...row, _key: 'perm-' + i }))} />

      <div className="card" style={{ padding: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>
          {t('users.scope')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          {scopes.map((it) => (
            <div key={it.role} style={{ padding: 12, borderLeft: '3px solid var(--brand-primary)', background: 'var(--surface-2)', borderRadius: '0 6px 6px 0' }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{t('roles.' + it.role)}</div>
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>{it.scope}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
